import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Music, Mic2 } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";
import { readSsgInitialData } from "../hooks/useSsgInitialData";

const BASE_URL = "https://allwhufc.com";

export default function ChantDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // Hydrate synchronously from the baked SSG shell so JS-rendering
  // Googlebot never sees an empty spinner (would trigger Soft 404).
  const initial = readSsgInitialData(slug);
  const [chant, setChant] = useState(initial && initial.title ? initial : null);
  const [loading, setLoading] = useState(!(initial && initial.title));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    // Reset state on slug change so a stale prior slug never bleeds through.
    // Without this, an SPA nav A→B where B's XHR 404s would leave A's
    // content visible under URL B (wrong canonical + duplicate content).
    // We seed from the shell blob for the CURRENT slug only.
    const fresh = readSsgInitialData(slug);
    setChant(fresh && fresh.title ? fresh : null);
    setLoading(!(fresh && fresh.title));
    setNotFound(false);
    axios
      .get(`${API}/chants/by-slug/${slug}`)
      .then((res) => alive && (setChant(res.data), setLoading(false)))
      .catch((err) => {
        if (!alive) return;
        // Only treat a genuine 404 as "not found". Timeouts, 5xx and network
        // failures should NOT flip the page into a "not found" state — that
        // would contradict the valid noscript shell + JSON-LD and cause
        // Google to de-index a page whose DB record actually exists.
        const status = err?.response?.status;
        if (status === 404) {
          setNotFound(true);
        }
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading || (!chant && !notFound)) {
    // Also cover the "API failed with non-404" case: render the loading
    // shell rather than overwriting the valid noscript body with an error.
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <SEO title="Loading..." path={`/chants/${slug}`} />
        <div className="flex items-center justify-center py-40"><div className="spinner" /></div>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="chant-notfound">
        <Navbar />
        <SEO
          title="Chant not found"
          path={`/chants/${slug}`}
          noindex={true}
        />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Mic2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-700 mb-2" style={{ fontFamily: "Barlow Condensed" }}>
            CHANT NOT FOUND
          </h1>
          <Link to="/chants" className="inline-flex items-center gap-2 text-[#2BAEE5] font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to all chants
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const metaDesc = (chant.description || chant.lyrics || chant.title || "")
    .slice(0, 160)
    .replace(/\s+/g, " ")
    .trim();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: chant.title,
    description: chant.description || undefined,
    url: `${BASE_URL}/chants/${chant.slug}`,
    genre: "Football Chant",
    about: { "@type": "SportsTeam", name: "West Ham United" },
    text: chant.lyrics,
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="chant-detail-page">
      <SEO
        title={`${chant.title} — West Ham Chant Lyrics`}
        path={`/chants/${chant.slug}`}
        description={metaDesc}
        type="article"
        jsonLd={jsonLd}
        seoTitle={chant.seo_title}
        seoDescription={chant.seo_description}
        keywords={chant.seo_keywords}
        imageAlt={chant.image_alt}
      />
      <Navbar />

      <div className="bg-[#7C2C3B] py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-3"
            data-testid="chant-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2 text-[#F8D742] text-xs font-semibold uppercase tracking-wide mb-2">
            <Music className="w-3.5 h-3.5" /> {chant.category || "Chant"}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "Barlow Condensed" }}>
            {chant.title?.toUpperCase()}
          </h1>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {chant.description && (
          <p className="text-slate-600 italic leading-relaxed mb-6">{chant.description}</p>
        )}

        <div className="bg-[#0F172A] rounded-xl p-8 mb-8">
          <h2 className="sr-only">Lyrics</h2>
          <pre className="text-white/90 font-sans text-base md:text-lg leading-relaxed whitespace-pre-wrap">
            {chant.lyrics}
          </pre>
        </div>

        <div className="mt-8">
          <SocialShareButtons shareText={`${chant.title} — a West Ham chant`} />
        </div>
      </article>

      <Footer />
    </div>
  );
}
